import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ICliente, IDispositivoAlarma, IQueryParam } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { DateRangeValue } from '../../../../../componentes/date-picker/date-picker.component';
import { EventosGenericosHttpService } from '../../../../../auxiliares/servicios/http/eventos-genericos.service';
import { DescargasService } from '../../../../navigation/descargas/service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { format } from 'date-fns';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';

const MAX_DIAS_RANGO = 31;
const MAX_ALARMAS_HIJOS = 300;

type AlarmaItem = {
  _id: string;
  idCliente: string;
  idsAncestros?: string[];
  nombre?: string;
  numeroAbonado?: string;
};

@Component({
  selector: 'app-exportaciones-alarmas',
  templateUrl: './exportaciones-alarmas.component.html',
  styleUrl: './exportaciones-alarmas.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class ExportacionesAlarmasComponent implements OnInit {
  private eventosService = inject(EventosGenericosHttpService);
  private descargasService = inject(DescargasService);
  private helper = inject(HelperService);
  private dialog = inject(DialogService);
  private lista2 = inject(Lista2Service);

  public cliente: ICliente | null = null;
  public incluirHijos = true;
  public incluirSinEventos = false;
  public incluirTest = false;
  public alarma: AlarmaItem | null = null;
  public rango: DateRangeValue | null = null;
  public exportando = false;
  public totalAlarmasConHijos: number | null = null;
  public cargandoAlarmas = true;

  public todasLasAlarmas: AlarmaItem[] = [];

  public get alarmasFiltradas(): AlarmaItem[] {
    if (!this.cliente) return this.todasLasAlarmas;
    return this.todasLasAlarmas.filter(
      (a) =>
        a.idCliente === this.cliente!._id ||
        a.idsAncestros?.includes(this.cliente!._id),
    );
  }

  public get hijosDeshabilitado(): boolean {
    return (
      this.totalAlarmasConHijos !== null &&
      this.totalAlarmasConHijos > MAX_ALARMAS_HIJOS
    );
  }

  public get rangoExcedeMes(): boolean {
    if (!this.rango?.start || !this.rango?.end) return false;
    const diff =
      new Date(this.rango.end).getTime() - new Date(this.rango.start).getTime();
    return diff > MAX_DIAS_RANGO * 24 * 60 * 60 * 1000;
  }

  async ngOnInit(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.lista2.dispositivosAlarmas(
          {
            select: '_id idCliente idsAncestros nombre numeroAbonado',
            includeChildren: true,
            unlimited: true,
          },
          0,
        ),
      );
      this.todasLasAlarmas = res.datos as AlarmaItem[];
    } catch {
      this.todasLasAlarmas = [];
    } finally {
      this.cargandoAlarmas = false;
    }
  }

  public alarmaLabel(a: AlarmaItem): string {
    const partes = [a.numeroAbonado, a.nombre].filter(Boolean);
    return partes.join(' - ') || a._id;
  }

  public alarmaSearchFn(term: string, a: AlarmaItem): boolean {
    const t = term.toLowerCase();
    return (
      (a.numeroAbonado?.toLowerCase().includes(t) ?? false) ||
      (a.nombre?.toLowerCase().includes(t) ?? false)
    );
  }

  public get exportLabel(): string {
    return this.alarma
      ? 'Exportar eventos de la alarma'
      : 'Exportar eventos del cliente';
  }

  public get canExport(): boolean {
    return (
      (!!this.cliente || !!this.alarma) &&
      !!this.rango?.start &&
      !!this.rango?.end &&
      !this.exportando &&
      !this.rangoExcedeMes
    );
  }

  public onClienteChange(c: ICliente | null): void {
    this.cliente = c;
    this.alarma = null;
    if (!c) {
      this.totalAlarmasConHijos = null;
      this.incluirHijos = true;
      return;
    }
    const count = this.todasLasAlarmas.filter(
      (a) => a.idCliente === c._id || a.idsAncestros?.includes(c._id),
    ).length;
    this.totalAlarmasConHijos = count;
    if (this.hijosDeshabilitado) this.incluirHijos = false;
  }

  public setRangoPreset(
    preset: 'esteMes' | 'mesPasado' | '1semana' | '48h' | '24h',
  ): void {
    const ahora = new Date();
    let start: Date;
    let end: Date = ahora;

    switch (preset) {
      case 'esteMes':
        start = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        break;
      case 'mesPasado':
        start = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
        end = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        break;
      case '1semana':
        start = new Date(ahora.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '48h':
        start = new Date(ahora.getTime() - 48 * 60 * 60 * 1000);
        break;
      case '24h':
        start = new Date(ahora.getTime() - 24 * 60 * 60 * 1000);
        break;
    }

    this.rango = { start: start!.toISOString(), end: end.toISOString() };
  }

  private buildConfirmMessage(): string {
    const desde = format(new Date(this.rango!.start), 'dd/MM/yyyy HH:mm');
    const hasta = format(new Date(this.rango!.end), 'dd/MM/yyyy HH:mm');

    const rows: { label: string; value: string }[] = [];

    if (this.cliente)
      rows.push({ label: 'Cliente', value: this.cliente.nombre });

    if (this.alarma) {
      rows.push({ label: 'Alarma', value: this.alarmaLabel(this.alarma) });
    } else {
      rows.push({
        label: 'Clientes hijos',
        value: this.incluirHijos ? 'Incluidos' : 'No incluidos',
      });
    }

    rows.push({ label: 'Desde', value: desde });
    rows.push({ label: 'Hasta', value: hasta });

    const tableRows = rows
      .map(
        (r) =>
          `<tr>
            <td style="padding:4px 12px 4px 0;color:var(--text-secondary-color);white-space:nowrap">${r.label}</td>
            <td style="padding:4px 0;font-weight:500">${escaparHtml(r.value)}</td>
          </tr>`,
      )
      .join('');

    return `<table style="border-collapse:collapse;margin-top:8px">${tableRows}</table>`;
  }

  public async exportar(): Promise<void> {
    if (!this.canExport) return;

    const title = this.alarma
      ? 'Exportar eventos de la alarma'
      : 'Exportar eventos del cliente';

    const confirmed = await this.dialog.confirm(
      title,
      this.buildConfirmMessage(),
      'Exportar',
      'Cancelar',
    );
    if (!confirmed) return;

    let nombre: string;
    let query: IQueryParam;

    if (this.alarma) {
      nombre = `Eventos alarma - ${this.alarmaLabel(this.alarma)}`;
      query = {
        filter: JSON.stringify({
          idEntidad: this.alarma._id,
          fechaCreacion: { $gte: this.rango!.start, $lte: this.rango!.end },
        }),
        includeChildren: true,
        sort: '-fechaCreacion',
      };
      this.descargasService.agregarDescargaExcel(nombre, () =>
        this.eventosService.exportarAlarmaIndividualComoBlob(
          query,
          this.incluirTest,
        ),
      );
    } else {
      nombre = `Eventos alarmas - ${this.cliente!.nombre}`;
      query = {
        filter: JSON.stringify({
          idCliente: this.cliente!._id,
          fechaCreacion: { $gte: this.rango!.start, $lte: this.rango!.end },
        }),
        includeChildren: this.incluirHijos,
        sort: '-fechaCreacion',
      };
      this.descargasService.agregarDescargaExcel(nombre, () =>
        this.eventosService.exportarAlarmasClienteComoBlob(
          query,
          this.incluirSinEventos,
        ),
      );
    }

    this.helper.notifSuccess('Exportación iniciada — ver panel de Descargas');
  }
}
